import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Ticket, ClipboardList, Hash, BookOpen, BarChart3, Settings, Users, Shield } from 'lucide-react';

const ALL = ['member', 'consultant', 'dr', 'dn', 'direction', 'support'];

const menuItems = [
  { key: 'home', icon: Home, path: '/', roles: ALL },
  { key: 'my_tickets', icon: Ticket, path: '/tickets', roles: ALL },
  { key: 'all_tickets', icon: ClipboardList, path: '/tickets/all', roles: ALL },
  { key: 'browse_hashtags', icon: Hash, path: '/hashtags', roles: ALL },
  { key: 'knowledge_base', icon: BookOpen, path: '/knowledge-base', roles: ALL },
  { key: 'dashboard', icon: BarChart3, path: '/dashboard', roles: ['dn', 'direction', 'support'] },
  { key: 'support_control', icon: Shield, path: '/support-control', roles: ['support'] },
  { key: 'user_management', icon: Users, path: '/admin/users', roles: ['direction'] },
  { key: 'settings', icon: Settings, path: '/settings', roles: ALL },
];

export default function Sidebar({ drawerOpen = false, onClose = () => {} }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const filteredItems = menuItems.filter(item => item.roles.includes(user?.role));
  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Ferme le drawer avec la touche Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, onClose]);

  function handleItemClick(path) {
    onClose(); // ferme le drawer avant navigation (mobile)
    navigate(path);
  }

  return (
    <>
      {/* Backdrop mobile — visible uniquement < lg quand drawer ouvert */}
      {drawerOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={onClose}
          className="lg:hidden fixed inset-0 z-30"
          style={{ background: 'rgba(0,0,0,0.55)', top: '57px' }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — drawer < lg, fixe sidebar ≥ lg */}
      <aside
        data-testid="app-sidebar"
        className={
          'fixed left-0 top-[57px] bottom-0 w-60 flex flex-col py-4 overflow-y-auto z-40 transition-transform duration-300 ease-in-out ' +
          (drawerOpen ? 'translate-x-0' : '-translate-x-full') +
          ' lg:translate-x-0'
        }
        style={{ background: '#0F0F1A', borderRight: '1px solid rgba(255,255,255,0.08)' }}
        aria-hidden={!drawerOpen ? 'true' : 'false'}
      >
        <nav className="flex-1 px-3 space-y-1">
          {filteredItems.map(item => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                data-testid={`sidebar-${item.key}`}
                onClick={() => handleItemClick(item.path)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  background: active ? 'rgba(247,127,0,0.1)' : 'transparent',
                  color: active ? '#F77F00' : '#9CA3AF',
                  borderLeft: active ? '3px solid #F77F00' : '3px solid transparent',
                  minHeight: '44px',
                }}
              >
                <Icon size={18} /><span>{t(item.key, lang)}</span>
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-3 mx-3 rounded-lg" style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(247,127,0,0.15)' }}>
              <span style={{ color: '#F77F00', fontWeight: 600, fontSize: 14 }}>{user?.name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs truncate" style={{ color: '#6B7280' }}>{t(user?.role, lang)}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
